// Packages
import axios, { AxiosError } from 'axios';
// Modules
import { API_URL, ENDPOINTS } from 'api/api.config';
import webStorage from 'modules/storage';
import { getTokenExpiryMs } from './tokenExpiry';
// Helpers
import showNotification from 'helpers/showNotification';
// Redux
import { store } from 'redux/store';
import { setSubscriptionAccess } from 'redux/user/userReducer';
// Interfaces and types
import { ISignInResData } from 'types/authTypes';
// I18n
import { t } from 'i18n';

//* Refresh tokens are single-use per session and every tab of the browser shares one session in
//* localStorage, so only one refresh may run at a time across tabs: two at once lose the session.
const LOCK_NAME = 'timego-token-refresh';
const LOCK_STORAGE_KEY = 'timeGoTokenRefreshLock';
const LOCK_POLL_MS = 50;
//* A hung refresh must not hold the lock, and with it every tab's requests, for ever.
const REFRESH_TIMEOUT_MS = 10_000;
//* Outlives the refresh timeout, so a lock is only taken over from a tab that died holding it.
const LOCK_TTL_MS = 15_000;
//* Background tabs run timers late (Chrome batches them to about once a minute), so an idle tab
//* refreshes a minute ahead; requests still refresh 30 s ahead (tokenExpiry.ts) as the backstop.
const REFRESH_AHEAD_MS = 60_000;
const SIGN_OUT_DELAY_MS = 2000;
//* The backend answers 401 INVALID_TOKEN (400 for a malformed token) when the session is gone.
//* Offline, timeouts, 5xx and 429 may pass on the next try and must not sign anyone out.
const SESSION_GONE_STATUSES = [400, 401];
const NO_REFRESH_TOKEN = 'NO_REFRESH_TOKEN';

interface IStorageLock {
  owner?: string;
  expiresAt?: number;
}

let refreshPromise: Promise<string> | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | undefined;

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const isStorageLockHeld = () =>
  (webStorage.getData<IStorageLock>(LOCK_STORAGE_KEY).expiresAt ?? 0) > Date.now();

//* ponytail: localStorage has no compare-and-set, so two tabs can still take it in the same
//* instant. Only browsers without Web Locks get here (Safari < 15.4, Firefox < 96), and the
//* re-reads in refreshUnderLock / recoverFromFailedRefresh catch most of what slips through.
const withStorageLock = async <T>(task: () => Promise<T>): Promise<T> => {
  const owner = `${Date.now()}-${Math.random()}`;

  while (isStorageLockHeld()) {
    await sleep(LOCK_POLL_MS);
  }

  webStorage.setData<IStorageLock>(
    { owner, expiresAt: Date.now() + LOCK_TTL_MS },
    LOCK_STORAGE_KEY,
  );

  try {
    return await task();
  } finally {
    if (webStorage.getData<IStorageLock>(LOCK_STORAGE_KEY).owner === owner) {
      webStorage.unsetData(LOCK_STORAGE_KEY);
    }
  }
};

const withRefreshLock = async <T>(task: () => Promise<T>): Promise<T> =>
  'locks' in navigator
    ? await navigator.locks.request(LOCK_NAME, task)
    : withStorageLock(task);

const storeSession = (data: ISignInResData) => {
  const { accountName } = webStorage.getData();
  const { tokens, role, ...restData } = data;

  webStorage.setData({
    ...tokens,
    ...restData,
    accountName,
  });

  if (typeof data.hasSubscriptionAccess === 'boolean') {
    store.dispatch(setSubscriptionAccess(data.hasSubscriptionAccess));
  }
};

//* The raw axios client bypasses the interceptors (no refresh loop on this very request).
const requestNewSession = async (refreshToken: string): Promise<string> => {
  const { data } = await axios.patch<ISignInResData>(
    `${API_URL}${ENDPOINTS.refreshToken}`,
    { refreshToken },
    { timeout: REFRESH_TIMEOUT_MS },
  );

  storeSession(data);

  return data.tokens.accessToken;
};

//* The notice stays on screen for SIGN_OUT_DELAY_MS before the sign-out; in that time the tab's own
//* refresh timer (armed for a token that is already expired) tries again and fails the same way,
//* which would show the notice twice.
let isSigningOut = false;

const signOutAfterNotice = () => {
  if (isSigningOut) {
    return;
  }

  isSigningOut = true;
  showNotification('error', t('mfa.invalidToken'));

  setTimeout(() => {
    webStorage.unsetData();
    webStorage.unsetData('timeGoUse12Hours');
    isSigningOut = false;
    window.location.reload();
    window.location.replace('/');
  }, SIGN_OUT_DELAY_MS);
};

//* A tab without the lock (e.g. one still on the previous release) may have rotated the session
//* while this request was in flight: the server rejects ours, but its tokens in storage are good.
const recoverFromFailedRefresh = (sentRefreshToken: string, error: AxiosError): string => {
  const latest = webStorage.getData();

  if (latest.refreshToken && latest.refreshToken !== sentRefreshToken) {
    return latest.accessToken;
  }

  if (SESSION_GONE_STATUSES.includes(error.response?.status ?? 0)) {
    signOutAfterNotice();
  }

  throw error;
};

const refreshUnderLock = async (staleAccessToken?: string): Promise<string> => {
  const current = webStorage.getData();

  if (!current.refreshToken) {
    throw new Error(NO_REFRESH_TOKEN);
  }

  //* Another tab refreshed while this one waited for the lock: its session is this tab's too.
  if (current.accessToken !== staleAccessToken) {
    return current.accessToken;
  }

  try {
    return await requestNewSession(current.refreshToken);
  } catch (error) {
    return recoverFromFailedRefresh(current.refreshToken, error as AxiosError);
  }
};

//* Resolves with an access token newer than `staleAccessToken` (the one the caller found expiring
//* or saw rejected). One in-flight refresh per tab, one at a time across tabs; signs out, after
//* the notice, only when the session is really gone.
export const refreshAccessToken = (staleAccessToken?: string): Promise<string> => {
  if (refreshPromise) {
    return refreshPromise;
  }

  //* No session to refresh, e.g. an anonymous visitor on a public form page: posting an undefined
  //* token would fail and wrongly report an expired session.
  if (!webStorage.getData().refreshToken) {
    return Promise.reject(new Error(NO_REFRESH_TOKEN));
  }

  refreshPromise = withRefreshLock(() => refreshUnderLock(staleAccessToken))
    .then(accessToken => {
      scheduleTokenRefresh();

      return accessToken;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

//* Arms this tab's timer for a minute before the stored access token expires (right away if that
//* is already past). Re-armed on every request, after every refresh, when another tab changes
//* storage and when the tab is shown again. Nothing is armed without a session.
export const scheduleTokenRefresh = (): void => {
  clearTimeout(refreshTimer);

  const { accessToken, refreshToken } = webStorage.getData();
  const expiryMs = accessToken && refreshToken ? getTokenExpiryMs(accessToken) : null;

  if (expiryMs === null) {
    return;
  }

  refreshTimer = setTimeout(() => {
    //* A failure already did what it should: sign-out after the notice, or wait for the next try.
    refreshAccessToken(accessToken).catch(() => undefined);
  }, Math.max(expiryMs - REFRESH_AHEAD_MS - Date.now(), 0));
};

const refreshIfShown = () => {
  if (document.visibilityState === 'visible') {
    scheduleTokenRefresh();
  }
};

//* Once per tab, at boot. Every tab arms its own timer; the lock lets only one of them refresh.
export const startTokenRefresh = (): void => {
  window.addEventListener('storage', scheduleTokenRefresh);
  document.addEventListener('visibilitychange', refreshIfShown);
  scheduleTokenRefresh();
};
